import { useInRouterContext } from 'react-router-dom';
import type { MutableRefObject } from 'react';
import { useNavigationGuard } from './useNavigationGuard';

type NavigationGuardProps = {
  shouldBlock: boolean;
  onProceed?: () => void;
  /** Raised true while the confirmation dialog is open so editing cells skip their focus-loss commit (DL-60097). */
  navBlockingRef?: MutableRefObject<boolean>;
};

/**
 * Inner component that calls useBlocker (requires a data router — i.e. the
 * app's createBrowserRouter + RouterProvider). Kept separate so we can skip
 * mounting it when no router context exists at all (e.g. Storybook, tests).
 */
const NavigationGuardInner = ({ shouldBlock, onProceed, navBlockingRef }: NavigationGuardProps) => {
  useNavigationGuard({ shouldBlock, onProceed, navBlockingRef });
  return null;
};

/**
 * Navigation guard that blocks route changes when there are unsaved inline edits.
 * Shows a confirmation dialog ("Stay" / "Leave") before allowing navigation.
 *
 * Safe to render outside a router (e.g. Storybook stories or tests without a
 * router wrapper) — renders nothing. When mounted inside the app's data router
 * (RouterProvider from createBrowserRouter) the guard is active.
 */
export const NavigationGuard = ({
  shouldBlock,
  onProceed,
  navBlockingRef,
}: NavigationGuardProps) => {
  const inRouter = useInRouterContext();
  if (!inRouter) return null;
  return (
    <NavigationGuardInner
      shouldBlock={shouldBlock}
      onProceed={onProceed}
      navBlockingRef={navBlockingRef}
    />
  );
};
